import { Component, OnInit, Input, Output, ViewChild, EventEmitter } from '@angular/core';
import { ProfileMin } from '../../../../../profiles/shared/profile-min.model';
import { UtilService, DialogService } from '../../../../../shared/index';
import { EventRead } from '../../../../shared/event-read.model';
import { RegistrationAttendee } from 'src/app/events-v4/shared/registration/registration-attendee.model';
import { RegistrationAttendeeItem } from 'src/app/events-v4/shared/registration/registration-attendee-item.model';
import { EventItemSummary } from 'src/app/events-v4/shared/items/event-item-summary.model';
import { AttendedFlagOrUnflag } from 'src/app/events-v4/shared/attendee/attended-flag-or-unflag.model';
import { IValidationComponent } from '../../../../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../../../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../../../../shared/translation/translation.service';
import { RegistrationCustomRegFieldResponse } from 'src/app/events-v4/shared/registration/registration-custom-reg-field-response.model';
import { ProfileDupCheckerComponent } from '../../../../../profiles/profile-dup-checker/profile-dup-checker.component';
import { EventCustomRegField } from '../../../../shared/custom-reg-fields/event-custom-reg-field.model';
import { EventFieldVisibleRequiredEnum } from '../../../../shared/event-field-visible-required.enum';
import { EventV4RegistrationCustomFieldsComponent } from '../../../event-v4-registration-custom-fields/event-v4-registration-custom-fields.component';
import { ProfileService } from '../../../../../profiles/shared/profile.service';
import { AsyncState, PagedResultGeneric } from '../../../../../shared/model/index';
import { EventV4AttendeeService } from 'src/app/events-v4/shared/attendee/event-v4-attendee.service';
import { EventV4ItemService } from 'src/app/events-v4/shared/items/event-v4-item.service';
import { EventV4CustomRegFieldService } from 'src/app/events-v4/shared/custom-reg-fields/event-v4-custom-reg-field.service';
import { EventV4RegistrationService } from 'src/app/events-v4/shared/registration/event-v4-registration.service';
import { EmailInputComponent } from '../../../../../shared/widgets/email-input/email-input.component';

@Component({
  selector: 'wl-event-v4-registration-wizard-attendee',
  templateUrl: 'event-v4-registration-wizard-attendee.component.html',
  styleUrls: ['event-v4-registration-wizard-attendee.component.css'],
  providers: [EventV4ItemService, EventV4CustomRegFieldService, EventV4AttendeeService],
  standalone: false,
})
export class EventV4RegistrationWizardAttendeeComponent implements OnInit, IValidationComponent {
  static ComponentUniqueId: number = 0;

  /**
   * the attendee this component manages
   */
  @Input() public get attendee(): RegistrationAttendee {
    return this._attendee;
  }
  public set attendee(value: RegistrationAttendee) {
    this._attendee = value;
    if (value) this._copiedValuesProfileId = value.ProfileId;

    this.updateSelectedItemsList();
  }

  /**
   * here just to trigger watches when the array mutates
   */
  @Input() public get eventItems(): Array<RegistrationAttendeeItem> {
    return this._attendeeEventItems;
  }
  public set eventItems(value: Array<RegistrationAttendeeItem>) {
    this._attendeeEventItems = value;
    this.updateSelectedItemsList();
  }

  @Input() public searchAllProfiles: boolean = false;
  @Input('customFields') public allPossibleCustomFields: Array<EventCustomRegField> = [];
  @Input() public editingRegistration: boolean = false;
  @Input() public registrationProfileId: number = 0;
  @Input() public isOrg = false;

  @Input() public event: EventRead = new EventRead();

  @Output() responseChange = new EventEmitter<RegistrationCustomRegFieldResponse>();
  @Output() onItemSelectionChanged = new EventEmitter<RegistrationAttendeeItem>();
  @Output() remove = new EventEmitter<RegistrationAttendee>();

  public _attendee: RegistrationAttendee = new RegistrationAttendee();
  public _attendeeEventItems: Array<RegistrationAttendeeItem> = [];
  public _eventFieldVisibleRequiredEnum: any = EventFieldVisibleRequiredEnum;
  public _selectedEventItemIds: Array<number> = [];
  public _copiedValuesProfileId: number = 0;
  public _lastChosenProfile: ProfileMin = null;
  public _uniqueId: number = 0;
  public _loadingRelatedOrgProfile: AsyncState = new AsyncState();

  public availableEventItems: Array<EventItemSummary>;
  public canAddNewAttendee: boolean;

  @ViewChild('profileIndDupChecker', { static: false }) profileIndDupChecker: ProfileDupCheckerComponent;
  @ViewChild('customFieldsComponent', { static: false }) public _customFieldsComponent: EventV4RegistrationCustomFieldsComponent;
  @ViewChild('emailInputComponent', { static: false }) public emailInputComponent: EmailInputComponent;

  public constructor(
    public _utilService: UtilService,
    public _eventItemService: EventV4ItemService,
    public _translationService: TranslationService,
    public _registrationService: EventV4RegistrationService,
    public _profileService: ProfileService,
    public _eventCustomRegFieldService: EventV4CustomRegFieldService,
    public _attendeeService: EventV4AttendeeService
  ) {
    this.searchAllProfiles = false;
    this.availableEventItems = new Array<EventItemSummary>();
    this.canAddNewAttendee = false;
  }

  ngOnInit() {
    this._uniqueId = EventV4RegistrationWizardAttendeeComponent.ComponentUniqueId++;
  }

  public getValidationResult(): ValidationResult {
    let valid = new ValidationResult();
    valid.isValid = true;

    if (this._customFieldsComponent) {
      valid = this._customFieldsComponent.getValidationResult();
      if (!valid.isValid)
        valid.errors.push(this._translationService.getInstant('events.registration.wizard.attendees.custom-reg-field-invalid-error'));
    }

    // only perform validation if we show it
    if (this.displayField(this.event.RegEmailVisible)) {
      if (
        this._utilService.isNotNullEmptyOrWhitespace(this.attendee.Email) &&
        !(this.emailInputComponent?.isValidEmail(this.attendee.Email) ?? true)
      ) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstantCapitalizeFirstCharacter('common.personal-information.email-invalid-message'));
      }
    }

    // 2026 02 11 - for now we are not enforcing email required
    // if (this.event.RegEmailRequired && this._utilService.isNullEmptyOrWhitespace(this.attendee.Email)) {
    //   valid.isValid = false;
    //   valid.errors.push(this._translationService.getInstantCapitalizeFirstCharacter('common.personal-information.email-required-message'));
    // }

    return valid;
  }

  public displayField(value: number): boolean {
    //this is an attendee so look for 1 or 3 (bitwise 2)
    if ((value & 2) > 0) {
      return true;
    } else {
      return false;
    }
  }

  public updateProfileInfo(profile: ProfileMin, attendee: RegistrationAttendee): void {
    if (profile.ProfileId > 0) {
      let currentFirstName = attendee.FName;
      let currentLastName = attendee.LName;
      let currentEmail = attendee.Email;
      let currentOrgName = attendee.OrgName;

      this._utilService.map(profile, attendee);

      // restore org name
      attendee.OrgName = currentOrgName;

      this._copiedValuesProfileId = attendee.ProfileId = profile.ProfileId;
      attendee.Phone = profile.WorkPhone;

      // get org to which this profile is the main contact if it's an individual
      if (!profile.OrgInd) {
        this._loadingRelatedOrgProfile.busy = true;
        this._loadingRelatedOrgProfile.success = false;
        this._loadingRelatedOrgProfile.message = '';

        this._profileService.getMainOrFirstRelatedOrg(profile.ProfileId).then(
          (orgP) => {
            this._loadingRelatedOrgProfile.busy = false;
            this._loadingRelatedOrgProfile.success = true;

            if (orgP && orgP.ProfileId > 0) attendee.OrgName = orgP.ReportName;
          },
          (error) => {
            this._loadingRelatedOrgProfile.busy = false;
            this._loadingRelatedOrgProfile.success = false;
            this._loadingRelatedOrgProfile.message = this._utilService.extractErrorMessage(error);
          }
        );
      }

      if (profile.OrgInd == true) {
        attendee.OrgName = profile.OrgName;
        attendee.FName = currentFirstName;
        attendee.LName = currentLastName;
        attendee.NameForBadge = (currentFirstName + ' ' + currentLastName).trim();
      } else {
        attendee.Prefix = profile.Prefix;
        attendee.Title = profile.Title;
        attendee.FName = profile.FirstName;
        attendee.LName = profile.LastName;
        attendee.NameForBadge = profile.ReportName;
      }
    }
  }

  public searchAllCheckboxChange() {
    if (!this.attendee.searchAllProfiles && !this.isOrg) {
      this.attendee.ProfileId = null;
    }
  }

  public profileChanged(profile: ProfileMin, attendee: RegistrationAttendee): void {
    this._lastChosenProfile = profile;
    // only automatically update profile if the attendee is new
    if (attendee.AttendeeId <= 0) this.updateProfileInfo(profile, attendee);
  }

  public relatedProfileChanged(profile: ProfileMin, attendee: RegistrationAttendee): void {
    this._lastChosenProfile = profile;
    // only automatically update profile if the attendee is new
    if (attendee.AttendeeId <= 0) this.updateProfileInfo(profile, attendee);
  }

  public attendeeItemSelectionChanged(item: RegistrationAttendeeItem): void {
    this.updateSelectedItemsList();
    this.onItemSelectionChanged.emit(item);
  }

  public onResponseChange(response: any): void {
    this.responseChange.emit(response);
  }

  public onIndivNameChange(attendee: RegistrationAttendee): void {
    if (attendee.AttendeeId > 0) return;

    let name = attendee.LName;
    if (this._utilService.isNotNullEmptyOrWhitespace(attendee.FName)) {
      name = attendee.FName + ' ' + attendee.LName;
    }

    // set name for badge
    attendee.NameForBadge = name;
  }

  /**
   * triggered when first or last name change
   * @param attendee
   */
  public onLastNameChange(attendee: RegistrationAttendee): void {
    if (attendee.ProfileId <= 0 && attendee.CreateProfile == true) {
      let name = attendee.LName;
      if (this._utilService.isNotNullEmptyOrWhitespace(attendee.FName)) {
        name = attendee.FName + ' ' + attendee.LName;
      }
      this.profileIndDupChecker.getPossibleDuplicates(name).then((data: PagedResultGeneric<ProfileMin>) => {
        if (data.Result.length > 0) {
          this.profileIndDupChecker.show();
        } else {
          this.profileIndDupChecker.hide();
        }
      });
    }
  }

  /**
   * force a refresh of the custom fields list
   */
  public refreshCustomFields(): void {
    this.updateSelectedItemsList();
  }

  public onDupCheckerIndNameProfileSelected(data: ProfileMin, attendee: RegistrationAttendee): void {
    this._lastChosenProfile = data;
    this.updateProfileInfo(data, attendee);
  }

  public updateSelectedItemsList() {
    this._selectedEventItemIds = this.attendee.Items.filter((eventItem) => eventItem.Selected).map((eventItem) => eventItem.EventItemId);
  }

  /**
   * manually request copying over of profile details
   */
  public handleCopyProfileInfoClick(): void {
    this.updateProfileInfo(this._lastChosenProfile, this.attendee);
  }

  /**
   * when the delete button is clicked
   */
  public handleRemoveAttendeeClick(): void {
    this.remove.emit(this.attendee);
  }

  public handleAttendedClick(attendee: RegistrationAttendee): void {
    if (attendee.AttendeeHeaderId > 0) {
      let dto: AttendedFlagOrUnflag = new AttendedFlagOrUnflag();
      let selectedEventItems = new Array<number>();
      for (let eventItem of this.eventItems) {
        if (eventItem.Selected == true) {
          selectedEventItems.push(eventItem.EventItemId);
        }
      }
      dto.Attended = attendee.Attended;
      dto.AttendeeHeaderIds = [attendee.AttendeeHeaderId];
      dto.EventItemIds = selectedEventItems;

      this._attendeeService.updateAttendedFlagForEventItems(dto);
    }
  }
}
